import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, X, ArrowLeft, Check, Loader2, Download, Sparkles, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";

// Before/After Comparison Slider Component
const BeforeAfterSlider = ({ beforeImage, afterImage, wrapName }) => {
  const containerRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);

  const handleTouchMove = useCallback((e) => {
    handleMove(e.touches[0].clientX);
  }, [handleMove]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  return (
    <div 
      ref={containerRef}
      className="relative w-full aspect-video rounded-lg overflow-hidden cursor-ew-resize select-none bg-black"
      onMouseDown={handleMouseDown}
      onTouchMove={handleTouchMove}
      onTouchStart={() => setIsDragging(true)}
      onTouchEnd={() => setIsDragging(false)}
      data-testid="before-after-slider"
    >
      {/* After Image (Full width, underneath) */}
      <img 
        src={afterImage} 
        alt="After - Wrapped" 
        className="absolute inset-0 w-full h-full object-contain"
        draggable={false}
      />
      
      {/* Before Image (Clipped) - using clip-path for perfect alignment */}
      <div 
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img 
          src={beforeImage} 
          alt="Before - Original" 
          className="w-full h-full object-contain"
          draggable={false}
        />
      </div>

      {/* Slider Line */}
      <div 
        className="absolute top-0 bottom-0 w-1 bg-premier-blue shadow-[0_0_10px_rgba(79,179,214,0.8)] cursor-ew-resize z-10"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        {/* Slider Handle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-premier-blue rounded-full flex items-center justify-center shadow-lg">
          <GripVertical className="w-5 h-5 text-white" />
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 bg-black/70 px-3 py-1 rounded-full">
        <span className="font-mono text-xs text-white uppercase tracking-wider">Before</span>
      </div>
      <div className="absolute top-4 right-4 bg-premier-blue/90 px-3 py-1 rounded-full">
        <span className="font-mono text-xs text-white uppercase tracking-wider">{wrapName}</span>
      </div>
    </div>
  );
};

const ConfiguratorPage = () => {
  const navigate = useNavigate();
  const [wraps, setWraps] = useState([]);
  const [selectedWrap, setSelectedWrap] = useState(null);
  const [carImage, setCarImage] = useState(null);
  const [carImageBase64, setCarImageBase64] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: "",
    email: "",
    phone: "",
    budget: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchWraps();
  }, []);

  const fetchWraps = async () => {
    try {
      const response = await api.get("/wraps");
      setWraps(response.data);
    } catch (error) {
      toast.error("Failed to load wrap options");
    }
  };

  const handleImageUpload = useCallback((file) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setCarImage(e.target.result);
      setCarImageBase64(e.target.result);
      setGeneratedImage(null);
      setShowComparison(false);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleImageUpload(file);
  }, [handleImageUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const generateWrap = async () => {
    if (!carImageBase64 || !selectedWrap) {
      toast.error("Please upload a car image and select a wrap");
      return;
    }

    setIsGenerating(true);
    setShowComparison(false);
    try {
      const response = await api.post("/generate-wrap", {
        car_image_base64: carImageBase64,
        wrap_name: selectedWrap.name,
        wrap_description: selectedWrap.description
      });

      if (response.data.success) {
        setGeneratedImage(response.data.generated_image);
        setShowComparison(true);
        toast.success("Wrap applied! Drag the slider to compare.");
      } else {
        toast.error(response.data.message || "Generation failed");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate wrap preview");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.post("/leads", {
        ...leadForm,
        car_image_url: carImage,
        selected_wrap_id: selectedWrap?.id,
        generated_image_url: generatedImage
      });
      toast.success("Thanks! We'll be in touch soon.");
      setShowLeadForm(false);
      setLeadForm({ name: "", email: "", phone: "", budget: "" });
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadImage = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `wrapped-car-${selectedWrap?.name || 'preview'}.png`;
    link.click();
  };

  const solidWraps = wraps.filter(w => w.category === 'solid');
  const patternWraps = wraps.filter(w => w.category === 'pattern');

  return (
    <div className="min-h-screen bg-premier-navy">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-heavy px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            className="text-white/60 hover:text-white"
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div className="font-unbounded text-lg font-bold tracking-tight">
            <img src="/logo.png" alt="Premier Signs & Wraps" className="h-10" />
          </div>
          <div className="w-24" /> {/* Spacer */}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Panel - Image Canvas */}
          <div className="space-y-6">
            <div>
              <h2 className="font-unbounded font-bold text-2xl mb-2">Your Vehicle</h2>
              <p className="font-manrope text-white/60">
                {showComparison ? "Drag the slider to compare before & after" : "Upload a photo of your car to get started"}
              </p>
            </div>

            {/* Upload Zone / Image Display / Comparison Slider */}
            <div 
              className={`relative aspect-video rounded-lg overflow-hidden ${
                !carImage ? 'upload-zone' : ''
              } ${isDragging ? 'drag-over' : ''}`}
              onDrop={!showComparison ? handleDrop : undefined}
              onDragOver={!showComparison ? handleDragOver : undefined}
              onDragLeave={!showComparison ? handleDragLeave : undefined}
            >
              {!carImage ? (
                <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer bg-premier-purple/50">
                  <Upload className="w-12 h-12 text-white/30 mb-4" />
                  <p className="font-manrope text-white/60 mb-2">Drag & drop your car image</p>
                  <p className="font-mono text-xs text-white/30">or click to browse</p>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                    data-testid="car-image-input"
                  />
                </label>
              ) : showComparison && generatedImage ? (
                <BeforeAfterSlider 
                  beforeImage={carImage}
                  afterImage={generatedImage}
                  wrapName={selectedWrap?.name || "Wrapped"}
                />
              ) : (
                <>
                  <img 
                    src={generatedImage || carImage} 
                    alt="Car preview" 
                    className="w-full h-full object-contain bg-black"
                  />
                  
                  {/* Processing overlay */}
                  <AnimatePresence>
                    {isGenerating && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="processing-overlay"
                      >
                        <div className="scan-line" />
                        <Loader2 className="w-12 h-12 text-premier-blue animate-spin mb-4" />
                        <p className="font-unbounded text-lg text-premier-blue">Applying Wrap...</p>
                        <p className="font-mono text-xs text-white/40 mt-2">AI is transforming your vehicle</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Clear button */}
                  <button
                    onClick={() => {
                      setCarImage(null);
                      setCarImageBase64(null);
                      setGeneratedImage(null);
                      setShowComparison(false);
                    }}
                    className="absolute top-4 right-4 p-2 bg-black/50 rounded-full hover:bg-premier-blue transition-colors"
                    data-testid="clear-image-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </>
              )}
              
              {/* Clear button for comparison mode */}
              {showComparison && (
                <button
                  onClick={() => {
                    setCarImage(null);
                    setCarImageBase64(null);
                    setGeneratedImage(null);
                    setShowComparison(false);
                  }}
                  className="absolute top-4 right-16 p-2 bg-black/50 rounded-full hover:bg-premier-blue transition-colors z-20"
                  data-testid="clear-comparison-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Action buttons */}
            {carImage && (
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={generateWrap}
                  disabled={!selectedWrap || isGenerating}
                  className="flex-1 btn-primary"
                  data-testid="generate-btn"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      {generatedImage ? "Regenerate" : "Generate Preview"}
                    </>
                  )}
                </Button>
                
                {generatedImage && (
                  <>
                    <Button
                      onClick={() => setShowComparison(!showComparison)}
                      className={`btn-secondary ${showComparison ? 'border-premier-blue text-premier-blue' : ''}`}
                      data-testid="toggle-comparison-btn"
                    >
                      {showComparison ? "Hide Compare" : "Compare"}
                    </Button>
                    <Button
                      onClick={downloadImage}
                      className="btn-secondary"
                      data-testid="download-btn"
                    >
                      <Download className="w-5 h-5" />
                    </Button>
                    <Button
                      onClick={() => setShowLeadForm(true)}
                      className="btn-primary"
                      data-testid="get-quote-btn"
                    >
                      Get Quote
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Wrap Selection */}
          <div className="space-y-6">
            <div>
              <h2 className="font-unbounded font-bold text-2xl mb-2">Select Wrap</h2>
              <p className="font-manrope text-white/60">Choose from our premium wrap options</p>
            </div>

            <Tabs defaultValue="solid" className="w-full">
              <TabsList className="w-full bg-premier-purple border border-white/10 p-1">
                <TabsTrigger 
                  value="solid" 
                  className="flex-1 data-[state=active]:bg-premier-blue data-[state=active]:text-white"
                  data-testid="solid-tab"
                >
                  Solid Colours
                </TabsTrigger>
                <TabsTrigger 
                  value="pattern" 
                  className="flex-1 data-[state=active]:bg-premier-blue data-[state=active]:text-white"
                  data-testid="pattern-tab"
                >
                  Patterns
                </TabsTrigger>
              </TabsList>

              <TabsContent value="solid" className="mt-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {solidWraps.map((wrap) => (
                    <WrapCard 
                      key={wrap.id} 
                      wrap={wrap} 
                      selected={selectedWrap?.id === wrap.id}
                      onClick={() => setSelectedWrap(wrap)}
                    />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="pattern" className="mt-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {patternWraps.map((wrap) => (
                    <WrapCard 
                      key={wrap.id} 
                      wrap={wrap} 
                      selected={selectedWrap?.id === wrap.id}
                      onClick={() => setSelectedWrap(wrap)}
                    />
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            {selectedWrap && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass p-6 rounded-lg"
              >
                <h3 className="font-unbounded font-bold text-lg mb-2">{selectedWrap.name}</h3>
                <p className="font-manrope text-white/60 text-sm">{selectedWrap.description}</p>
              </motion.div>
            )}
          </div>
        </div>
      </main>

      {/* Lead Form Modal */}
      <AnimatePresence>
        {showLeadForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80"
            onClick={() => setShowLeadForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass-heavy w-full max-w-md p-8 rounded-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-unbounded font-bold text-2xl mb-2">Get Your Quote</h3>
              <p className="font-manrope text-white/60 mb-6">Fill in your details and we'll get back to you with a custom quote.</p>
              
              <form onSubmit={handleLeadSubmit} className="space-y-4">
                <div>
                  <Label className="form-label">Name</Label>
                  <Input
                    required
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                    placeholder="John Smith"
                    className="h-12"
                    data-testid="lead-name-input"
                  />
                </div>
                <div>
                  <Label className="form-label">Email</Label>
                  <Input
                    required
                    type="email"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    placeholder="john@example.com"
                    className="h-12"
                    data-testid="lead-email-input"
                  />
                </div>
                <div>
                  <Label className="form-label">Phone</Label>
                  <Input
                    required
                    type="tel"
                    value={leadForm.phone}
                    onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    className="h-12"
                    data-testid="lead-phone-input"
                  />
                </div>
                <div>
                  <Label className="form-label">Budget Range</Label>
                  <Input
                    required
                    value={leadForm.budget}
                    onChange={(e) => setLeadForm({ ...leadForm, budget: e.target.value })}
                    placeholder="e.g., $2,000 - $3,000"
                    className="h-12"
                    data-testid="lead-budget-input"
                  />
                </div>
                
                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 btn-secondary"
                    onClick={() => setShowLeadForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 btn-primary"
                    data-testid="submit-lead-btn"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const WrapCard = ({ wrap, selected, onClick }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`wrap-card relative aspect-square rounded-xl overflow-hidden border-2 ${
      selected ? 'selected border-premier-blue' : 'border-white/10'
    }`}
    data-testid={`wrap-card-${wrap.id}`}
  >
    <img 
      src={wrap.thumbnail_url} 
      alt={wrap.name}
      className="w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
    <div className="absolute bottom-0 left-0 right-0 p-3">
      <p className="font-manrope font-semibold text-sm text-white">{wrap.name}</p>
    </div>
    {selected && (
      <div className="absolute top-2 right-2 w-6 h-6 bg-premier-blue rounded-full flex items-center justify-center">
        <Check className="w-4 h-4" />
      </div>
    )}
  </motion.button>
);

export default ConfiguratorPage;
